import type {
  FlowFormData,
  ResolveStepAutoFill,
  StepAutoFillResult,
} from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { ComuniTopic } from '../constants';
import { stripTopicPrefix, TOPIC_SEPARATOR, withTopicPrefix } from './topicPrefix';

const NAME_FIELD_KEYS = ['template_name', 'element_name'] as const;

const collectNameFieldStrips = (data: FlowFormData, topic: ComuniTopic): FlowFormData => {
  const next: FlowFormData = {};
  const prefix = `${topic}${TOPIC_SEPARATOR}`;
  for (const key of NAME_FIELD_KEYS) {
    const value = data[key];
    if (typeof value === 'string' && value.startsWith(prefix)) {
      next[key] = stripTopicPrefix(value);
    }
  }
  return next;
};

/** Show only the editable title in the form; topic prefix is attached on submit. */
export const stripLockedTopicFromNameFields = (data: FlowFormData, topic: ComuniTopic): FlowFormData => {
  const next: FlowFormData = { ...data };
  for (const key of NAME_FIELD_KEYS) {
    const value = next[key];
    if (typeof value === 'string') {
      next[key] = stripTopicPrefix(withTopicPrefix(topic, value));
    }
  }
  return next;
};

export const stripLockedTopicFromInitialFormData = (
  initialFormData: Record<string, FlowFormData> | undefined,
  topic: ComuniTopic
): Record<string, FlowFormData> | undefined => {
  if (!initialFormData) return undefined;
  return Object.fromEntries(
    Object.entries(initialFormData).map(([stepKey, stepData]) => [stepKey, stripLockedTopicFromNameFields(stepData, topic)])
  );
};

const nameRawFromParams = (data: FlowFormData, suffix?: string): string => {
  if (suffix !== undefined) return suffix;
  if (typeof data['template_name'] === 'string' && data['template_name']) return data['template_name'];
  if (typeof data['element_name'] === 'string' && data['element_name']) return data['element_name'];
  return '';
};

/** Attach the invisible topic prefix onto name fields before createInstance. */
export const applyLockedTopicToFlowParams = (data: FlowFormData, topic: ComuniTopic, suffix?: string): FlowFormData => {
  const fullName = withTopicPrefix(topic, nameRawFromParams(data, suffix));
  const next: FlowFormData = { ...data, ['template_name']: fullName };
  if (typeof data['element_name'] === 'string' || 'element_name' in data) {
    next['element_name'] = fullName;
  }
  return next;
};

/**
 * For Edit flows loaded from the connector: strip a locked topic prefix from name fields
 * so the form only shows the editable title (same UX as Customize).
 */
export const withLockedTopicNameStrip = (
  handler: ResolveStepAutoFill | undefined,
  lockedTopic: ComuniTopic | undefined
): ResolveStepAutoFill | undefined => {
  if (!handler && !lockedTopic) return undefined;

  return (ctx): StepAutoFillResult | null => {
    const base = handler?.(ctx) ?? null;
    if (!lockedTopic) return base;

    if (base?.skip) {
      return { data: stripLockedTopicFromNameFields(base.data, lockedTopic), skip: true };
    }

    const schemaProps = (ctx.formSchema.schema?.properties ?? {}) as Record<string, { default?: unknown }>;
    const candidate: FlowFormData = { ...ctx.accumulatedData, ...(base?.data ?? {}) };
    for (const key of NAME_FIELD_KEYS) {
      if (typeof candidate[key] === 'string') continue;
      const def = schemaProps[key]?.default;
      if (typeof def === 'string') candidate[key] = def;
    }

    const strips = collectNameFieldStrips(candidate, lockedTopic);
    if (Object.keys(strips).length === 0) return base;

    return { data: { ...(base?.data ?? {}), ...strips }, skip: false };
  };
};
